import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FiDownload } from 'react-icons/fi';
import InvoicePageShell from './components/InvoicePageShell';
import Snackbar from '../Admin-Subscriptions/components/Snackbar';
import { subscriptionAdminApi, getApiErrorMessage } from '../subscriptionAdmin/api';
import { canWrite } from '../subscriptionAdmin/permissions';
import { formatInr } from '../subscriptionAdmin/razorpay';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DetailField({ label, value }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ color: '#0f172a', fontWeight: 500, mt: 0.25 }}>{value ?? '—'}</Typography>
    </Box>
  );
}

function getLineItems(invoice) {
  if (Array.isArray(invoice?.lineItems)) return invoice.lineItems;
  if (Array.isArray(invoice?.items)) return invoice.items;
  return [];
}

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });
  const canIssue = canWrite('invoices');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await subscriptionAdminApi.getInvoice(id);
      setInvoice(data);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async () => {
    try {
      await subscriptionAdminApi.issueInvoice(id);
      setSnackbar({ open: true, type: 'success', message: 'Invoice issued.' });
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const blob = await subscriptionAdminApi.downloadInvoicePdf(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice?.invoiceNumber || `invoice-${String(id).slice(-8)}`}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      setDownloading(false);
    }
  };

  const lineItems = getLineItems(invoice);
  const invoiceNumber = invoice?.invoiceNumber || String(id).slice(-8);

  return (
    <InvoicePageShell
      title={loading ? 'Invoice' : `Invoice ${invoiceNumber}`}
      subtitle={invoice?.status ? `Status: ${invoice.status}` : 'Invoice details'}
      backTo="/invoice/view"
      actions={
        !loading &&
        invoice && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canIssue && invoice.status !== 'issued' && (
              <Button variant="contained" size="small" onClick={issue}>
                Issue invoice
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<FiDownload />}
              onClick={downloadPdf}
              disabled={downloading}
            >
              {downloading ? 'Downloading…' : 'Download PDF'}
            </Button>
          </Box>
        )
      }
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !invoice ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Invoice not found or you do not have access.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/invoice/view')}>
            Back to invoices
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                {invoiceNumber}
              </Typography>
              <Typography sx={{ color: '#64748b', mt: 0.5 }}>
                Created {formatDate(invoice.createdAt)}
              </Typography>
            </Box>
            <Chip
              label={invoice.status || 'unknown'}
              color={invoice.status === 'issued' ? 'success' : 'default'}
              sx={{ alignSelf: 'flex-start' }}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#334155', fontWeight: 700 }}>
                Bill to
              </Typography>
              <DetailField label="Customer" value={invoice.customerName || invoice.billingName} />
              <DetailField label="Email" value={invoice.customerEmail || invoice.billingEmail} />
              <DetailField
                label="Address"
                value={
                  invoice.billingAddress ||
                  [invoice.billingCity, invoice.billingState, invoice.billingPincode]
                    .filter(Boolean)
                    .join(', ')
                }
              />
              <DetailField label="GSTIN" value={invoice.gstin || invoice.customerGstin} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#334155', fontWeight: 700 }}>
                Invoice info
              </Typography>
              <DetailField label="User ID" value={String(invoice.userId || '—')} />
              <DetailField label="Contract ID" value={String(invoice.contractId || '—')} />
              <DetailField label="Subscription ID" value={String(invoice.subscriptionId || '—')} />
              <DetailField label="Issued at" value={formatDate(invoice.issuedAt)} />
              <DetailField label="Due date" value={formatDate(invoice.dueDate)} />
            </Grid>
          </Grid>

          {lineItems.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#334155', fontWeight: 700 }}>
                Line items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item, index) => (
                    <TableRow key={item._id || index}>
                      <TableCell>{item.description || item.name || '—'}</TableCell>
                      <TableCell align="right">{item.quantity ?? 1}</TableCell>
                      <TableCell align="right">{formatInr(item.rate ?? item.unitPrice)}</TableCell>
                      <TableCell align="right">{formatInr(item.amount ?? item.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ minWidth: 240 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography>{formatInr(invoice.subtotal ?? invoice.amount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Tax</Typography>
                <Typography>{formatInr(invoice.taxAmount ?? invoice.gstAmount)}</Typography>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700 }}>Total</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  {formatInr(invoice.totalAmount ?? invoice.amount)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </InvoicePageShell>
  );
}
